import React, { useEffect } from 'react';
import {
    Alert,
    Box,
    Button,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    Skeleton,
    TextField,
    ThemeProvider,
    Typography,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import LockResetIcon from '@mui/icons-material/LockReset';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import api from '../../services/api';
import lightTheme from '../../themes/lightTheme';

const MIN_PASSWORD_LEN = 8

const CAMPO_VAZIO = { current_password: '', new_password: '', confirm_password: '' }

/** Linha "rótulo: valor" dos dados da conta. */
function Info({ label, value, loading }) {
    return (
        <Grid item xs={12} sm={6}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {label}
            </Typography>
            {loading
                ? <Skeleton width="60%" />
                : <Typography variant="body1">{value || '—'}</Typography>}
        </Grid>
    );
}

/** Campo de senha com botão de mostrar/ocultar. */
function PasswordField({ show, onToggleShow, ...props }) {
    return (
        <TextField
            {...props}
            fullWidth
            margin="normal"
            type={show ? 'text' : 'password'}
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton
                            onClick={onToggleShow}
                            edge="end"
                            tabIndex={-1}
                            aria-label={show ? 'Ocultar senha' : 'Mostrar senha'}
                        >
                            {show ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                    </InputAdornment>
                ),
            }}
        />
    );
}

export default function ProfilePage() {
    const [profile, setProfile] = React.useState(null);
    const [loading, setLoading] = React.useState(true);

    const [form, setForm] = React.useState(CAMPO_VAZIO);
    const [errors, setErrors] = React.useState({});
    const [show, setShow] = React.useState({
        current_password: false, new_password: false, confirm_password: false,
    });
    const [saving, setSaving] = React.useState(false);
    const [alert, setAlert] = React.useState(null);

    useEffect(() => {
        document.title = 'IPV7 Tools - Meu perfil';
    }, []);

    useEffect(() => {
        let ativo = true;
        api.get('profile/')
            .then(res => { if (ativo) setProfile(res.data) })
            .catch(() => {
                if (ativo) setAlert({
                    severity: 'error',
                    msg: 'Não foi possível carregar seus dados.',
                });
            })
            .finally(() => { if (ativo) setLoading(false) });
        return () => { ativo = false };
    }, []);

    const onChange = (campo) => (event) => {
        const value = event.target.value;
        setForm(f => ({ ...f, [campo]: value }));
        setErrors(e => ({ ...e, [campo]: undefined }));
        setAlert(null);
    };

    const toggleShow = (campo) => () => setShow(s => ({ ...s, [campo]: !s[campo] }));

    const senhasConferem = form.new_password === form.confirm_password;
    const podeEnviar = (
        !saving
        && form.current_password.length > 0
        && form.new_password.length >= MIN_PASSWORD_LEN
        && form.confirm_password.length >= MIN_PASSWORD_LEN
        && senhasConferem
    );

    const validar = () => {
        const novos = {};
        if (!form.current_password) {
            novos.current_password = 'Informe sua senha atual.';
        }
        if (form.new_password.length < MIN_PASSWORD_LEN) {
            novos.new_password = `A nova senha deve ter no mínimo ${MIN_PASSWORD_LEN} caracteres.`;
        }
        if (form.new_password && form.new_password === form.current_password) {
            novos.new_password = 'A nova senha deve ser diferente da senha atual.';
        }
        if (!senhasConferem) {
            novos.confirm_password = 'As senhas não conferem.';
        }
        setErrors(novos);
        return Object.keys(novos).length === 0;
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        setAlert(null);
        if (saving || !validar()) return;

        setSaving(true);
        api.put('profile/password', form).then(res => {
            // Erro de validação vem como 200 + {"errors": [{field, msg}]}.
            const apiErrors = res.data?.errors;
            if (apiErrors?.length) {
                setErrors(apiErrors.reduce((acc, e) => ({ ...acc, [e.field]: e.msg }), {}));
                return;
            }
            setForm(CAMPO_VAZIO);
            setErrors({});
            setAlert({ severity: 'success', msg: res.data?.message || 'Senha alterada com sucesso.' });
        }).catch(err => {
            const status = err.response?.status;
            if (status === 429) {
                setAlert({
                    severity: 'warning',
                    msg: 'Muitas tentativas seguidas. Aguarde alguns minutos e tente novamente.',
                });
            } else if (!status) {
                setAlert({
                    severity: 'error',
                    msg: 'Não foi possível falar com o servidor. Verifique sua conexão e tente novamente.',
                });
            } else {
                setAlert({ severity: 'error', msg: 'Não foi possível alterar sua senha.' });
            }
        }).finally(() => setSaving(false));
    };

    return (
        <ThemeProvider theme={lightTheme}>
            <Box sx={{
                bgcolor: 'background.default',
                color: 'text.primary',
                minHeight: 'calc(100vh - 100px)',
                p: { xs: 1, md: 2 },
            }}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h4" sx={{ fontFamily: 'Montserrat', fontWeight: 300 }}>
                        Meu perfil
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Seus dados de acesso e troca de senha.
                    </Typography>
                </Box>

                <Paper elevation={0} sx={{
                    p: 3, mb: 3, maxWidth: 720,
                    bgcolor: 'background.paper',
                    border: '1px solid', borderColor: 'divider', borderRadius: 2,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                        <PersonIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                        <Typography variant="h6" sx={{ fontFamily: 'Montserrat', fontWeight: 500 }}>
                            Dados da conta
                        </Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Grid container spacing={2}>
                        <Info label="Nome" value={profile?.name} loading={loading} />
                        <Info label="Usuário" value={profile?.username} loading={loading} />
                        <Info label="Email" value={profile?.email} loading={loading} />
                        <Info label="Cargo" value={profile?.role} loading={loading} />
                    </Grid>
                </Paper>

                <Paper
                    elevation={0}
                    component="form"
                    onSubmit={handleSubmit}
                    noValidate
                    sx={{
                        p: 3, maxWidth: 720,
                        bgcolor: 'background.paper',
                        border: '1px solid', borderColor: 'divider', borderRadius: 2,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                        <LockResetIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                        <Typography variant="h6" sx={{ fontFamily: 'Montserrat', fontWeight: 500 }}>
                            Alterar senha
                        </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Digite sua senha atual e a nova senha duas vezes para confirmar.
                    </Typography>
                    <Divider sx={{ my: 2 }} />

                    <PasswordField
                        required
                        label="Senha atual"
                        name="current_password"
                        autoComplete="current-password"
                        value={form.current_password}
                        onChange={onChange('current_password')}
                        error={Boolean(errors.current_password)}
                        helperText={errors.current_password || ' '}
                        show={show.current_password}
                        onToggleShow={toggleShow('current_password')}
                        disabled={saving}
                    />
                    <PasswordField
                        required
                        label="Nova senha"
                        name="new_password"
                        autoComplete="new-password"
                        value={form.new_password}
                        onChange={onChange('new_password')}
                        error={Boolean(errors.new_password)}
                        helperText={errors.new_password || `Mínimo de ${MIN_PASSWORD_LEN} caracteres.`}
                        show={show.new_password}
                        onToggleShow={toggleShow('new_password')}
                        disabled={saving}
                    />
                    <PasswordField
                        required
                        label="Confirme a nova senha"
                        name="confirm_password"
                        autoComplete="new-password"
                        value={form.confirm_password}
                        onChange={onChange('confirm_password')}
                        error={Boolean(errors.confirm_password) || (form.confirm_password.length > 0 && !senhasConferem)}
                        helperText={
                            errors.confirm_password
                            || (form.confirm_password.length > 0 && !senhasConferem ? 'As senhas não conferem.' : ' ')
                        }
                        show={show.confirm_password}
                        onToggleShow={toggleShow('confirm_password')}
                        disabled={saving}
                    />

                    {alert && (
                        <Alert severity={alert.severity} sx={{ mt: 2 }}>{alert.msg}</Alert>
                    )}

                    <Box sx={{ mt: 3 }}>
                        <Button type="submit" variant="contained" disabled={!podeEnviar}>
                            {saving ? 'Salvando...' : 'Alterar senha'}
                        </Button>
                    </Box>
                </Paper>
            </Box>
        </ThemeProvider>
    );
}
